import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '@/store/auth-store'
import { authService } from '@/services/auth-service'

const DEMO_ACCOUNTS = [
  { email: 'admin@pacificcoastpaving.com', label: 'Carlos Rivera', role: 'Admin' },
  { email: 'manager@pacificcoastpaving.com', label: 'Sarah Chen', role: 'Manager' },
  { email: 'mike@pacificcoastpaving.com', label: 'Mike Torres', role: 'Estimator' },
  { email: 'jessica@pacificcoastpaving.com', label: 'Jessica Park', role: 'Estimator' },
]

const DEMO_PASSWORD = 'demo123'
const DEMO_DOMAIN = '@pacificcoastpaving.com'

export function DemoAccountSwitcher() {
  const { user, login } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Only show for demo tenant
  if (!user?.email?.endsWith(DEMO_DOMAIN)) return null

  const currentAccount = DEMO_ACCOUNTS.find(a => a.email === user.email)

  async function handleSwitch(account: typeof DEMO_ACCOUNTS[0]) {
    if (account.email === user?.email || switching) return
    setSwitching(true)
    try {
      const result = await authService.login({ email: account.email, password: DEMO_PASSWORD })
      login(result.user, result.token)
      setOpen(false)
      // Reload to reset all stores and UI state
      window.location.href = '/dashboard'
    } catch (err) {
      console.error('Demo switch failed:', err)
    } finally {
      setSwitching(false)
    }
  }

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="demo-switcher" ref={ref}>
      <button
        className="demo-switcher-trigger"
        onClick={() => setOpen(!open)}
        disabled={switching}
      >
        <span className="demo-switcher-badge">DEMO</span>
        <span className="demo-switcher-name">{currentAccount?.label}</span>
        <span className="demo-switcher-role">{currentAccount?.role}</span>
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="demo-switcher-dropdown">
          <div className="demo-switcher-header">Switch Demo Account</div>
          {DEMO_ACCOUNTS.map(account => (
            <button
              key={account.email}
              className={`demo-switcher-option ${account.email === user?.email ? 'active' : ''}`}
              onClick={() => handleSwitch(account)}
              disabled={account.email === user?.email || switching}
            >
              <div className="demo-switcher-option-info">
                <span className="demo-switcher-option-name">{account.label}</span>
                <span className="demo-switcher-option-email">{account.email}</span>
              </div>
              <span className="demo-switcher-option-role">{account.role}</span>
              {account.email === user?.email && (
                <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="16" height="16" className="demo-switcher-check">
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
